/**
 * One student's detail (brief §teacher: `/teacher/students/:id`): recent
 * answer accuracy and topic coverage per chapter (`shared/mastery.ts`, via the
 * server DTO), the evidence block (topic states and assisted counts, never the
 * student's confidence ratings; pedagogy spec §13 Q2), the 50 most recent
 * attempts and exam history, plus role management — promote or demote,
 * except yourself (server-enforced, spec §4.9).
 */
import { useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  ProgressBar,
  Select,
  Spinner,
  Table,
} from "@client/components/ui";
import { useToast } from "@client/components/ui";
import { useAuth } from "@client/app/auth";
import { useLocalizedText } from "@client/components/Localized";
import { paperLabel } from "@client/features/exam/paper";
import { useLang, useT, useTp } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { formatDate, formatNumber, formatPercent, formatPoints } from "@client/lib/format";
import { previewStatement } from "@client/lib/preview";
import { verdictTone } from "@client/lib/verdict";
import { StudentEvidenceSection } from "./StudentEvidenceSection";
import { TeacherNav } from "./TeacherNav";
import { useChangeRole, useTeacherStudent } from "./api";
import type { RecentAttempt, StudentExamEntry } from "./types";

export function TeacherStudentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const localize = useLocalizedText();
  const toast = useToast();
  const { user: me } = useAuth();
  const query = useTeacherStudent(id);
  const changeRole = useChangeRole();
  const [pendingRole, setPendingRole] = useState<"student" | "teacher" | "">("");

  async function applyRoleChange() {
    if (!id || !pendingRole) return;
    try {
      await changeRole.mutateAsync({ id, role: pendingRole });
      toast.success(t("teacher.detail.role.success"));
      setPendingRole("");
    } catch (error) {
      toast.error(messageForError(error, t));
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        eyebrow={
          <Link to="/teacher/students" className="inline-flex items-center gap-1 hover:underline">
            <ArrowLeft className="h-3 w-3" />
            {t("teacher.students.title")}
          </Link>
        }
        title={query.data?.user.displayName ?? t("teacher.detail.title")}
        description={query.data?.user.email}
      />
      <TeacherNav />

      {query.isLoading && <Spinner size="md" label={t("common.loading")} />}
      {!query.isLoading && !query.data && <EmptyState title={t("common.error")} />}

      {query.data && (
        <>
          <section className="mb-8">
            <Card title={t("teacher.detail.role.title")} compact>
              {query.data.user.id === me?.id ? (
                <p className="text-sm text-ink-600 dark:text-ink-400">
                  {t("teacher.detail.role.self")}
                </p>
              ) : (
                <div className="flex flex-wrap items-end gap-3">
                  <Select
                    label={t("teacher.detail.role.label")}
                    options={[
                      { value: "student", label: t("teacher.role.student") },
                      { value: "teacher", label: t("teacher.role.teacher") },
                    ]}
                    value={pendingRole || query.data.user.role}
                    onChange={(e) => setPendingRole(e.target.value as "student" | "teacher")}
                  />
                  <Button
                    variant="primary"
                    loading={changeRole.isPending}
                    disabled={!pendingRole || pendingRole === query.data.user.role}
                    onClick={applyRoleChange}
                  >
                    {t("teacher.detail.role.apply")}
                  </Button>
                </div>
              )}
            </Card>
          </section>

          <section className="mb-8">
            <h2 className="mb-3 font-display text-lg text-ink-900 dark:text-ink-50">
              {t("teacher.detail.chapters.title")}
            </h2>
            <div className="space-y-2">
              {query.data.chapters.map((chapter) => (
                <div key={chapter.chapter}>
                  <div className="flex items-center gap-3">
                    <span className="w-20 shrink-0 text-sm text-ink-600 dark:text-ink-400">
                      {t("teacher.chapterLabel")} {chapter.chapter}
                    </span>
                    <ProgressBar
                      value={chapter.mastery}
                      ariaLabel={`${t("teacher.chapterLabel")} ${chapter.chapter}`}
                      valueText={formatPercent(chapter.mastery, lang)}
                      className="flex-1"
                    />
                    <span className="w-14 shrink-0 text-right text-sm tabular-nums text-ink-700 dark:text-ink-200">
                      {formatPercent(chapter.mastery, lang)}
                    </span>
                    <span className="w-20 shrink-0 text-right text-xs text-ink-500 dark:text-ink-400">
                      {tp("teacher.detail.chapters.attempts", chapter.attempts)}
                    </span>
                  </div>
                  {chapter.topicsTotal > 0 && (
                    <p className="mt-0.5 pl-[5.75rem] text-xs text-ink-500 dark:text-ink-400">
                      {t("teacher.detail.chapters.coverage", {
                        practised: chapter.topicsPractised,
                        total: chapter.topicsTotal,
                      })}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>

          <StudentEvidenceSection evidence={query.data.evidence} />

          <section className="mb-8">
            <h2 className="mb-3 font-display text-lg text-ink-900 dark:text-ink-50">
              {t("teacher.detail.exams.title")}
            </h2>
            {query.data.exams.length === 0 ? (
              <p className="text-sm text-ink-600 dark:text-ink-400">
                {t("teacher.detail.exams.empty")}
              </p>
            ) : (
              <ul>
                {query.data.exams.map((exam: StudentExamEntry) => (
                  <li
                    key={exam.id}
                    className="flex items-center justify-between gap-3 border-b border-ink-100 py-2 last:border-0 dark:border-ink-900"
                  >
                    <div>
                      <p className="text-sm font-medium text-ink-800 dark:text-ink-100">
                        {paperLabel(exam, t) ?? t("teacher.examKind.generated")}
                      </p>
                      <p className="text-xs text-ink-500 dark:text-ink-400">
                        {formatDate(exam.startedAt, lang)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {exam.totalScore !== null && (
                        <span className="text-sm tabular-nums">
                          {formatPoints(exam.totalScore, lang)}
                        </span>
                      )}
                      {exam.verdict ? (
                        <Badge tone={verdictTone(exam.verdict)}>{exam.verdict}</Badge>
                      ) : (
                        <Badge>{t(`teacher.examStatus.${exam.status}`)}</Badge>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2 className="mb-3 font-display text-lg text-ink-900 dark:text-ink-50">
              {t("teacher.detail.recentAttempts.title")}
            </h2>
            <Table<RecentAttempt>
              columns={[
                {
                  key: "statement",
                  header: t("teacher.detail.recentAttempts.question"),
                  cell: (row) => (
                    <span>
                      <span className="text-xs text-ink-500 dark:text-ink-400">
                        {t("teacher.chapterLabel")} {row.chapter} ·{" "}
                      </span>
                      {/* No dictionary entry fits "question no longer exists" —
                          the id is the next-best identifier for the teacher. */}
                      {row.statement ? previewStatement(localize(row.statement)) : row.questionId}
                      {row.kind === "retry" ? (
                        <Badge tone="warning" className="ml-2">
                          {t("teacher.detail.recent.retry")}
                        </Badge>
                      ) : row.assisted ? (
                        <Badge tone="warning" className="ml-2">
                          {t("teacher.detail.recent.assisted")}
                        </Badge>
                      ) : null}
                    </span>
                  ),
                },
                {
                  key: "mode",
                  header: t("teacher.detail.recentAttempts.mode"),
                  cell: (row) => t(`teacher.mode.${row.mode}`),
                },
                {
                  key: "gradedBy",
                  header: t("teacher.detail.recentAttempts.gradedBy"),
                  cell: (row) => t(`teacher.gradedBy.${row.gradedBy}`),
                },
                {
                  key: "score",
                  header: t("teacher.detail.recentAttempts.score"),
                  align: "right",
                  cell: (row) =>
                    row.score === null || row.maxScore === null
                      ? "—"
                      : `${formatNumber(row.score, lang)} / ${formatNumber(row.maxScore, lang)}`,
                },
                {
                  key: "createdAt",
                  header: t("teacher.detail.recentAttempts.date"),
                  align: "right",
                  cell: (row) => formatDate(row.createdAt, lang),
                },
              ]}
              rows={query.data.recentAttempts}
              rowKey={(row) => row.id}
              empty={
                <p className="text-sm text-ink-600 dark:text-ink-400">
                  {t("teacher.detail.recentAttempts.empty")}
                </p>
              }
            />
          </section>
        </>
      )}
    </div>
  );
}

export default TeacherStudentDetailPage;
